import React from "react";
import PropTypes from "prop-types";
import "./MonthlySummaryBoxes.css"; // Ensure proper syntax for the CSS import

const MonthlySummaryBoxes = ({
  income = [],
  expenses = [],
  budget = [], // NEW: Budget data from Firestore
  totalSpent = 0,
  remainingBalance = null,
  showProjections = false,
  projectedDrawdown = null,
  targetDailySpending = null, // NEW: Target daily spending for zero drawdown
  daysUntilNegative = null, // NEW: Days until balance goes negative
  avgDailySpend = 0, // NEW: Average daily spending from BigQuery
  transactionCount = 0, // NEW: Total transaction count
  avgTransactionAmount = 0, // NEW: Average transaction amount
  projectedTotal = 0, // NEW: Projected monthly total
}) => {
  const totalIncome = income.reduce((sum, inc) => sum + inc.amount, 0);
  const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);
  const totalBudget = budget.reduce((sum, budg) => sum + budg.amount, 0);

  // NEW: Calculate available discretionary funds after fixed expenses and budget
  const discretionaryFunds = totalIncome - totalExpenses - totalBudget;
  const budgetSurplusDeficit = discretionaryFunds; // Positive = surplus, Negative = deficit

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatOptionalCurrency = (amount) => {
    if (amount === null || amount === undefined) return "--";
    return formatCurrency(amount);
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 mb-6">
      <div className="summary-box bg-white p-2 sm:p-4 rounded-lg shadow-md flex flex-col items-center justify-center">
        <div className="bg-green-100 p-1 sm:p-2 rounded-full mb-1 sm:mb-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 sm:h-6 sm:w-6 text-green-500"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M10 18a8 8 0 100-16 8 8 0 000 16z" />
          </svg>
        </div>
        <div className="text-lg sm:text-2xl font-bold text-green-500 text-center">
          {formatCurrency(totalIncome)}
        </div>
        <div className="text-xs sm:text-sm text-gray-500 text-center">Monthly Income</div>
      </div>

      <div className="summary-box bg-white p-2 sm:p-4 rounded-lg shadow-md flex flex-col items-center justify-center">
        <div className="bg-red-100 p-1 sm:p-2 rounded-full mb-1 sm:mb-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 sm:h-6 sm:w-6 text-red-500"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M10 18a8 8 0 100-16 8 8 0 000 16z" />
          </svg>
        </div>
        <div className="text-lg sm:text-2xl font-bold text-red-500 text-center">
          {formatCurrency(totalExpenses)}
        </div>
        <div className="text-xs sm:text-sm text-gray-500 text-center">Monthly Fixed Expenses</div>
      </div>

      {/* Total CC Spent */}
      <div className="summary-box bg-white p-2 sm:p-4 rounded-lg shadow-md flex flex-col items-center justify-center">
        <div className="bg-orange-100 p-1 sm:p-2 rounded-full mb-1 sm:mb-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 sm:h-6 sm:w-6 text-orange-500"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M3 10h2l3-8 4 16 3-8h2" />
          </svg>
        </div>
        <div className="text-lg sm:text-2xl font-bold text-orange-500 text-center">
          {formatCurrency(totalSpent)}
        </div>
        <div className="text-xs sm:text-sm text-gray-500 text-center">Total CC Spent</div>
      </div>

      <div
        className={`summary-box bg-white p-2 sm:p-4 rounded-lg shadow-md flex flex-col items-center justify-center ${
          showProjections && projectedDrawdown !== null && projectedDrawdown >= 0
            ? "text-green-500"
            : showProjections && projectedDrawdown !== null
              ? "text-red-500"
              : "text-gray-500"
        }`}
      >
        <div
          className={`${
            showProjections && projectedDrawdown !== null && projectedDrawdown >= 0
              ? "bg-green-100"
              : showProjections && projectedDrawdown !== null
                ? "bg-red-100"
                : "bg-gray-100"
          } p-1 sm:p-2 rounded-full mb-1 sm:mb-2`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 sm:h-6 sm:w-6"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M10 18a8 8 0 100-16 8 8 0 000 16z" />
          </svg>
        </div>
        <div className="text-lg sm:text-2xl font-bold text-center">
          {formatOptionalCurrency(projectedDrawdown)}
        </div>
        <div className="text-xs sm:text-sm text-gray-500 text-center">
          {showProjections ? "Savings Drawdown" : "Savings Drawdown (MTD only)"}
        </div>
      </div>

      <div
        className={`summary-box bg-white p-2 sm:p-4 rounded-lg shadow-md flex flex-col items-center justify-center ${
          showProjections && remainingBalance !== null && remainingBalance >= 0
            ? "text-green-500"
            : showProjections && remainingBalance !== null
              ? "text-red-500"
              : "text-gray-500"
        }`}
      >
        <div
          className={`${
            showProjections && remainingBalance !== null && remainingBalance >= 0
              ? "bg-green-100"
              : showProjections && remainingBalance !== null
                ? "bg-red-100"
                : "bg-gray-100"
          } p-2 rounded-full mb-2`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M10 18a8 8 0 100-16 8 8 0 000 16z" />
          </svg>
        </div>
        <div className="text-2xl font-bold">
          {formatOptionalCurrency(remainingBalance)}
        </div>
        <div className="text-sm text-gray-500">
          {showProjections ? "Remaining Balance" : "Remaining Balance (MTD only)"}
        </div>
      </div>

      {/* NEW: Budget Surplus/Deficit */}
      <div
        className={`summary-box bg-white p-4 rounded-lg shadow-md flex flex-col items-center justify-center ${
          budgetSurplusDeficit >= 0 ? "text-green-500" : "text-red-500"
        }`}
      >
        <div
          className={`${
            budgetSurplusDeficit >= 0 ? "bg-green-100" : "bg-red-100"
          } p-2 rounded-full mb-2`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        </div>
        <div className="text-2xl font-bold">
          {formatCurrency(budgetSurplusDeficit)}
        </div>
        <div className="text-sm text-gray-500">
          {budgetSurplusDeficit >= 0 ? "Budget Surplus" : "Budget Deficit"}
        </div>
      </div>

      {/* Daily Avg Spending */}
      <div className="summary-box bg-white p-4 rounded-lg shadow-md flex flex-col items-center justify-center">
        <div className="bg-purple-100 p-2 rounded-full mb-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 text-purple-500"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="text-2xl font-bold text-purple-500">
          {formatCurrency(avgDailySpend)}
        </div>
        <div className="text-sm text-gray-500">Daily Avg Spending</div>
      </div>

      {/* Target Daily Spending */}
      <div className="summary-box bg-white p-4 rounded-lg shadow-md flex flex-col items-center justify-center">
        <div className="bg-blue-100 p-2 rounded-full mb-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 text-blue-500"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M10 18a8 8 0 100-16 8 8 0 000 16z" />
          </svg>
        </div>
        <div className="text-2xl font-bold text-blue-500">
          {showProjections && targetDailySpending !== null
            ? formatCurrency(targetDailySpending)
            : "--"}
        </div>
        <div className="text-sm text-gray-500">Target Daily Spend</div>
      </div>

      {/* NEW: Days Until Negative Box */}
      <div
        className={`summary-box bg-white p-4 rounded-lg shadow-md flex flex-col items-center justify-center ${
          !showProjections
            ? "text-gray-500"
            : daysUntilNegative === null || daysUntilNegative > 14
            ? "text-green-500"
            : daysUntilNegative > 7
            ? "text-yellow-500"
            : "text-red-500"
        }`}
      >
        <div
          className={`${
            !showProjections
              ? "bg-gray-100"
              : daysUntilNegative === null || daysUntilNegative > 14
              ? "bg-green-100"
              : daysUntilNegative > 7
              ? "bg-yellow-100"
              : "bg-red-100"
          } p-2 rounded-full mb-2`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M10 18a8 8 0 100-16 8 8 0 000 16z" />
          </svg>
        </div>
        <div className="text-2xl font-bold">
          {!showProjections
            ? "--"
            : daysUntilNegative === -1
            ? "Over"
            : daysUntilNegative === null
            ? "--"
            : `${daysUntilNegative}d`}
        </div>
        <div className="text-sm text-gray-500">
          {!showProjections
            ? "Days Until Negative (MTD only)"
            : daysUntilNegative === -1
            ? "Already Over"
            : "Days Until Negative"}
        </div>
      </div>

      {/* Transactions Count */}
      <div className="summary-box bg-white p-4 rounded-lg shadow-md flex flex-col items-center justify-center">
        <div className="bg-yellow-100 p-2 rounded-full mb-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7zm2-8a1 1 0 100 2h.01a1 1 0 100-2H9z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="text-2xl font-bold text-yellow-500">{transactionCount}</div>
        <div className="text-sm text-gray-500">Transactions</div>
      </div>

      {/* Average Transaction Amount */}
      <div className="summary-box bg-white p-4 rounded-lg shadow-md flex flex-col items-center justify-center">
        <div className="bg-cyan-100 p-2 rounded-full mb-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-cyan-500" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V4a2 2 0 00-2-2H6zm1 2a1 1 0 000 2h6a1 1 0 100-2H7zm6 7a1 1 0 011 1v3a1 1 0 11-2 0v-3a1 1 0 011-1zm-3 3a1 1 0 100 2h.01a1 1 0 100-2H10zm-4 1a1 1 0 011-1h.01a1 1 0 110 2H7a1 1 0 01-1-1zm1-4a1 1 0 100 2h.01a1 1 0 100-2H7zm2 1a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1zm4-4a1 1 0 100 2h.01a1 1 0 100-2H13zM9 9a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1zM7 8a1 1 0 000 2h.01a1 1 0 000-2H7z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="text-2xl font-bold text-cyan-500">{formatCurrency(avgTransactionAmount)}</div>
        <div className="text-sm text-gray-500">Avg. Transaction</div>
      </div>

      {/* Projected Monthly Total */}
      <div className={`summary-box bg-white p-4 rounded-lg shadow-md flex flex-col items-center justify-center ${
        !showProjections
          ? "text-gray-500"
          : projectedTotal > 6000
            ? "text-red-500"
            : projectedTotal < 5000
              ? "text-green-500"
              : "text-orange-500"
      }`}>
        <div className={`${
          !showProjections
            ? "bg-gray-100"
            : projectedTotal > 6000
              ? "bg-red-100"
              : projectedTotal < 5000
                ? "bg-green-100"
                : "bg-orange-100"
        } p-2 rounded-full mb-2`}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0zm-7.536 5.879a1 1 0 001.415 0 3 3 0 014.242 0 1 1 0 001.415-1.415 5 5 0 00-7.072 0 1 1 0 000 1.415z" clipRule="evenodd" />
          </svg>
        </div>

        <div className="text-2xl font-bold">
          {showProjections ? formatCurrency(projectedTotal) : formatCurrency(totalSpent)}
        </div>
        <div className="text-sm text-gray-500">
          {showProjections ? "Projected Monthly Total" : "Period CC Total"}
        </div>
      </div>
    </div>
  );
};

MonthlySummaryBoxes.propTypes = {
  income: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number.isRequired,
    }),
  ).isRequired,
  expenses: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number.isRequired,
    }),
  ).isRequired,
  budget: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number.isRequired,
    }),
  ).isRequired,
  totalSpent: PropTypes.number.isRequired,
  remainingBalance: PropTypes.number,
  showProjections: PropTypes.bool,
  projectedDrawdown: PropTypes.number,
  targetDailySpending: PropTypes.number,
  daysUntilNegative: PropTypes.number,
  avgDailySpend: PropTypes.number,
  transactionCount: PropTypes.number,
  avgTransactionAmount: PropTypes.number,
  projectedTotal: PropTypes.number,
};

export default MonthlySummaryBoxes;
